<script lang="ts" setup>
import { computed, nextTick, onBeforeMount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import Draggable from 'vuedraggable'
import axios from 'axios'
import dayjs from 'dayjs'
import { v4 } from 'uuid'
import { debouncedWatch, useLocalStorage } from '@vueuse/core'
import { fromPairs } from 'lodash'

import {
    DefaultRemark,
    RoutesForm,
    LoadingList,
    LoadingListRoute,
    RoutesFormRoute,
    LoadingListRouteItem,
    RouteDraft,
} from '@/types/cargo-loading.js'
import useForm from '@/hooks/use-form'
import { DropdownStringOption, MyInputRef, MySelectRef } from '@/types/inputs'
import { ResourceResponse } from '@/types/general'
import { useAuthStore } from '@/stores/auth-store'
import { emitter } from '@/utils/mitt'
import { numberFormatter } from '@/utils/numbers'
import { useLazyLoadedList } from '@/hooks/use-lazy-loaded-list'
import { MinimalCompany } from '@/types/company'
import { useConfirm } from '@/hooks/use-confirm'
import { formatRouteInfo, fullProviderNumber, nameAndShortCodeString } from '@/utils/cargo-loading'
import { datetime } from '@/utils/dates'

import MyForm from '@/components/my-components/form/MyForm.vue'
import MySelect from '@/components/my-components/form/MySelect.vue'
import MyInput from '@/components/my-components/form/MyInput.vue'
import MyButton from '@/components/my-components/MyButton.vue'
import MyErrorMessage from '@/components/my-components/form/MyErrorMessage.vue'
import RouteCard from '@/components/cargo-loading/RouteCard.vue'

interface Props {
    loadingList: LoadingList
}

interface UnassignedRouteOption extends DropdownStringOption {
    route: LoadingListRoute
}

interface CarrierOption extends DropdownStringOption {
    accountNumber: string | null
}

const isEditing = defineModel<boolean>('isEditing', { default: false })
const props = defineProps<Props>()

const emit = defineEmits<{
    (e: 'cancel'): void
    (e: 'save'): void
}>()

const { t } = useI18n()
const authStore = useAuthStore()
const confirm = useConfirm()

const { data, errors, submit } = useForm<RoutesForm>({
    routes: [],
})

const customerCompanies = useLazyLoadedList<CarrierOption>(fetchCustomers)
const loadingListRouteDrafts = useLocalStorage<RouteDraft>('loadingListRouteDrafts', {})
const unassignedRoutes = ref<LoadingListRoute[]>([])
const wrapperRef = ref<HTMLDivElement>()
const awbInputRefs = ref<MyInputRef[]>([])
// For some reason Vue can't hold multiple refs for this so we'll just use the latest value
const unassignedRouteSelectRefs = ref<MySelectRef | undefined>()
const defaultRemarks = ref<DefaultRemark[]>([])
const originalRoutes = ref<RoutesFormRoute[]>([])

const routeIds = computed(() => data.routes.map((route) => route.id))
const unassignedRouteOptions = computed<UnassignedRouteOption[]>(() => {
    return (
        unassignedRoutes.value
            // We'll filter out the routes that has already be added
            .filter((route) => !routeIds.value.includes(route.id))
            .map((route) => ({
                label: formatRouteInfo(route.destinationPort.shortName, route),
                value: route.id,
                route: route,
            }))
    )
})
const destinationPortOptions = computed<DropdownStringOption[]>(() => {
    return props.loadingList.destinationPorts.map((port) => ({
        value: port.id,
        label: nameAndShortCodeString(port.name, port.shortName),
    }))
})
const keyedDestinationPorts = computed(() => {
    return fromPairs(props.loadingList.destinationPorts.map((port) => [port.id, port.shortName]))
})

const listNumber = computed(() => {
    const dateFormatted = dayjs(props.loadingList.departedAt).format('DDMMMYYYY').toUpperCase()
    return `${fullProviderNumber(props.loadingList)} / ${dateFormatted}`
})

const listBarcodeUrl = computed(
    () => `https://barcodeapi.org/api/128/${encodeURIComponent(listNumber.value)}`,
)

const totals = computed(() => {
    const totals = {
        totalAwbs: 0,
        totalAmount: 0,
        totalWeight: 0,
        totalVolume: 0,
    }

    for (const route of data.routes) {
        for (const item of route.items) {
            totals.totalAwbs += 1
            totals.totalAmount += item.amount
            totals.totalWeight += item.weight
            totals.totalVolume += item.volume
        }
    }

    return totals
})

async function addRoute() {
    console.log('addRoute', props.loadingList.destinationPorts)
    data.routes.push({
        // We don't need the id's so we can leave them blank
        id: '',
        loadingListId: '',
        remark: '',
        carrierCompanyId: '',
        carrierNumber: '',
        // Dummy object to avoid errors
        carrier: { id: '', name: '', vat: '', options: { currency: '' } },
        carrierAccountNumber: null,
        route: '',
        order: data.routes.length,
        destinationPortId: props.loadingList.destinationPorts[0].id,
        departedAt: props.loadingList.departedAt,
        items: [],
    })
    await nextTick()
    unassignedRouteSelectRefs.value?.focus()
}

async function addItem(routeIndex: number) {
    data.routes[routeIndex].items.push({
        id: '',
        loadingListRouteId: '',
        loadingInstruction: null,
        name: '',
        awbNumber: '',
        amount: 0,
        weight: 0,
        volume: 0,
        description: '',
        spls: [],
    })
    await nextTick()
    awbInputRefs.value[awbInputRefs.value.length - 1]?.input.focus()
}

async function deleteRoute(index: number) {
    try {
        await confirm(
            t('deleteEntityTitle', { entity: t('route') }),
            t('areYouSureYouWantToDeleteIt'),
            {
                confirmText: t('yes'),
                cancelText: t('no'),
            },
        )
    } catch {
        return
    }

    data.routes.splice(index, 1)
}

async function deleteItem(routeIndex: number, itemIndex: number) {
    try {
        await confirm(
            t('deleteEntityTitle', { entity: t('item') }),
            t('areYouSureYouWantToDeleteIt'),
            {
                confirmText: t('yes'),
                cancelText: t('no'),
            },
        )
    } catch {
        return
    }
    data.routes[routeIndex].items.splice(itemIndex, 1)
}

function fetchFormData() {
    if (!isEditing.value) return

    fetchUnassignedRoutes()
    fetchDefaultRemarks()
    customerCompanies.fetch()

    originalRoutes.value = [...props.loadingList.routes.map(mapFormRoute)]

    // When we open the form we load the draft if any exists for this loading list
    if (loadingListRouteDrafts.value[props.loadingList.id]) {
        data.routes = [...loadingListRouteDrafts.value[props.loadingList.id].routes]
    }
}

async function fetchUnassignedRoutes() {
    if (unassignedRoutes.value.length > 0) return

    const params = {
        company: authStore.companyId,
        'with-items': true,
        filter: {
            'start-date': dayjs(props.loadingList.departedAt).startOf('day').toISOString(),
            'end-date': dayjs(props.loadingList.departedAt)
                .add(1, 'day')
                .endOf('day')
                .toISOString(),
            state: 'unlinkedRoutes',
            'port-ids': props.loadingList.destinationPorts.map((port) => port.id),
        },
        'per-page': 2000,
    }
    const response = await axios.get<ResourceResponse<LoadingListRoute[]>>(
        window.route('cl.company.loading-list-routes.index', params),
    )

    unassignedRoutes.value = response.data.data
}

async function fetchDefaultRemarks() {
    const response = await axios.get<ResourceResponse<DefaultRemark[]>>(
        window.route('cl.company.default-remarks.index', {
            company: authStore.companyId,
            'per-page': 2000,
            filter: { routes: true, 'provider-company-id': props.loadingList.provider.id },
        }),
    )
    defaultRemarks.value = response.data.data
}

/**
 * Finds a default remark matching the provider and carrier and sets it on the route
 */
function fillRouteRemark(routeIndex: number) {
    const route = data.routes[routeIndex]
    const defaultRemark = defaultRemarks.value.find((remark) => {
        return (
            remark.carrier?.id === route.carrierCompanyId &&
            remark.provider.id === props.loadingList.provider.id
        )
    })
    data.routes[routeIndex].remark = defaultRemark?.remark ?? ''
}

async function onRouteSelected(routeId: string | null, routeIndex: number) {
    const route = unassignedRoutes.value.find((r) => r.id === routeId)
    if (route) {
        data.routes[routeIndex] = mapFormRoute(route)
    } else {
        // When we can't find the route we'll assume that it's because the user wants to create a new route
        data.routes[routeIndex].id = v4()
        // In case they input the full carrier + number we split to match carrier on the words and put the numbers in the carrier number
        const match = (routeId || '').match(/^([A-Za-z]+)(\d+)$/)
        const carrier = match ? (match[1] ?? routeId) : routeId
        data.routes[routeIndex].carrierNumber = match ? (match[1] ?? '') : ''
        const existingCompany = customerCompanies.items.value.find((company) => {
            return (
                company.label === carrier ||
                company.accountNumber?.toLowerCase() === carrier?.toLowerCase() ||
                company.value === routeId
            )
        })
        data.routes[routeIndex].carrierCompanyId = existingCompany?.value ?? null
    }
    // Focus the carrier input so they can quickly continue
    // We can't use ref because the v-for is generated by Draggable therefore it won't use the ref as an array
    await nextTick()
    const carrierInputs = (wrapperRef.value?.querySelectorAll('.carrier-input') ??
        []) as HTMLInputElement[]
    carrierInputs[carrierInputs.length - 1]?.focus()

    fillRouteRemark(routeIndex)
}

async function onSubmit() {
    const formData = {
        routes: data.routes
            // Filter out any routes that hasn't been added properly
            .filter((route) => !!route.id)
            // Ensure the order of the routes are updated
            .map((route, index) => {
                route.order = index

                return route
            }),
    }
    const route = window.route('cl.company.loading-lists.update-routes', {
        company: authStore.companyId,
        loading_list: props.loadingList.id,
    })
    const response = await submit('PUT', route, {
        data: formData,
    })

    if (response) {
        emitter.emit('fetchLoadingLists')
        emit('save')
    }
}

function mapFormRoute(route: LoadingListRoute): RoutesFormRoute {
    return {
        ...route,
        carrierCompanyId: route.carrier.id,
        destinationPortId: route.destinationPort.id,
    }
}

async function fetchCustomers(): Promise<CarrierOption[]> {
    const response = await axios.get<MinimalCompany[]>(
        window.route('minimal.companies.customers', {
            company: authStore.companyId,
            'without-new': true,
        }),
    )
    return response.data.map((company) => ({
        label: nameAndShortCodeString(company.name, company.accountNumber ?? null),
        value: company.id,
        accountNumber: company.accountNumber ?? null,
    }))
}

function cancelEdit() {
    emit('cancel')
}

// Since we're tight on space we just use a normal input where the user types the spls as a comma-separated string
// By using a getter / setter we can automatically split to an array when the input is changed
function splsAsString(loadingListItem: LoadingListRouteItem) {
    return {
        get value() {
            return loadingListItem.spls ? loadingListItem.spls.join(', ') : ''
        },
        set value(newValue: string) {
            loadingListItem.spls = newValue
                .split(',')
                .map((spl) => spl.trim().toUpperCase())
                .filter((spl) => spl.length > 0)
        },
    }
}

function saveDraft() {
    // In case the form data matches the original loading list's routes we'll remove the draft
    // As they've not changed anything
    if (JSON.stringify(data.routes) === JSON.stringify(originalRoutes.value)) {
        delete loadingListRouteDrafts.value[props.loadingList.id]
        return
    }

    loadingListRouteDrafts.value[props.loadingList.id] = {
        routes: [...data.routes],
        timestamp: dayjs().toISOString(),
    }
}

onBeforeMount(() => {
    data.routes = props.loadingList.routes.map(mapFormRoute)

    fetchFormData()
})
watch(isEditing, fetchFormData)
watch(
    () => props.loadingList.routes,
    () => {
        data.routes = props.loadingList.routes.map(mapFormRoute)
    },
)
debouncedWatch(
    () => data.routes,
    () => {
        if (isEditing.value) saveDraft()
    },
    // Saves every 0.5 seconds
    { deep: true, debounce: 500 },
)
</script>

<template>
    <div ref="wrapperRef" class="space-y-3 text-primary-500 dark:text-primary-100">
        <!-- print-only barcode -->
        <div class="hidden print:block h-8 print:h-16 overflow-hidden">
            <img :src="listBarcodeUrl" alt="Barcode" class="block h-full" />
        </div>

        <div class="flex gap-4">
            <div>
                <span class="font-semibold" v-text="t('loadingDate')" />
                {{ datetime(props.loadingList.loadedAt) }}
            </div>

            <div>
                <span class="font-semibold" v-text="t('departureDate')" />
                {{ datetime(props.loadingList.departedAt) }}
            </div>
        </div>

        <!-- GENERAL INFO -->
        <div
            class="grid grid-cols-4 gap-4 text-sm border border-stone-300 dark:border-stone-600 rounded-lg p-4 bg-white dark:bg-dark-500 shadow-sm print:shadow-none"
        >
            <div class="flex flex-col">
                <span class="text-xs text-stone-700 dark:text-primary-300 font-medium">
                    {{ t('listNumber') }}
                </span>
                <span class="text-sm font-bold" v-text="listNumber" />
            </div>

            <div class="flex flex-col">
                <span class="text-xs text-stone-700 dark:text-primary-300 font-medium">
                    {{ t('tripNumber') }}
                </span>
                <span class="text-sm font-bold">{{ props.loadingList.tripNumber }}</span>
            </div>

            <div class="flex flex-col">
                <span class="text-xs text-stone-700 dark:text-primary-300 font-medium">
                    {{ props.loadingList.flightNumber ? t('flight') : t('truckTrailer') }}
                </span>
                <span class="text-sm font-bold">
                    <template v-if="props.loadingList.flightNumber">
                        {{ props.loadingList.flightNumber }}
                    </template>
                    <template
                        v-else-if="props.loadingList.truckNumber || props.loadingList.trailerNumber"
                    >
                        {{ props.loadingList.truckNumber }} / {{ props.loadingList.trailerNumber }}
                    </template>
                    <template v-else> - </template>
                </span>
            </div>

            <div class="flex flex-col">
                <span class="text-xs text-stone-700 dark:text-primary-300 font-medium">
                    {{ t('user') }}
                </span>
                <span class="text-sm font-bold">
                    {{ props.loadingList.user?.name }}
                </span>
            </div>

            <div class="flex flex-col">
                <span class="text-xs text-stone-700 dark:text-primary-300 font-medium">
                    {{ t('country') }}
                </span>
                <span class="text-sm font-bold">{{ props.loadingList.country }}</span>
            </div>

            <div class="flex flex-col col-span-3">
                <span class="text-xs text-stone-700 dark:text-primary-300 font-medium">
                    {{ t('originDestinations') }}
                </span>
                <span class="text-sm font-bold">
                    {{ props.loadingList.originPort.shortName }} /
                    {{ props.loadingList.destinationPorts.map((port) => port.shortName).join(',') }}
                </span>
            </div>
            <div class="flex flex-col col-span-2">
                <span class="text-xs text-stone-700 dark:text-primary-300 font-medium">
                    {{ t('remark') }}
                </span>
                <span class="text-sm font-bold">
                    {{ props.loadingList.remark }}
                </span>
            </div>
        </div>

        <MyForm :errors="errors" @submit.prevent="onSubmit">
            <Draggable
                v-model="data.routes"
                item-key="id"
                handle=".handle"
                ghost-class="drag-ghost-card"
                class="space-y-4"
            >
                <template #item="{ element: loadingRoute, index: routeIndex }">
                    <RouteCard
                        class="[&.sortable-chosen.drag-ghost-card]:p-0"
                        :destination="keyedDestinationPorts[loadingRoute.destinationPortId] ?? ''"
                        :route="loadingRoute"
                        :hide-items="!loadingRoute.id"
                    >
                        <template v-if="isEditing" #routeInformation>
                            <mdi:drag
                                class="absolute -left-6 handle mb-2 cursor-grab text-xl text-dark-700 transition dark:text-white"
                            />

                            <MySelect
                                v-if="!loadingRoute.id"
                                ref="unassignedRouteSelectRefs"
                                v-model="loadingRoute.id"
                                :options="unassignedRouteOptions"
                                :name="`routes.${routeIndex}.id`"
                                :label="t('findRoute')"
                                class="!ml-0"
                                searchable
                                creatable
                                :disabled="!!loadingRoute.id"
                                @change="onRouteSelected($event as string, routeIndex)"
                            />

                            <div v-else class="flex w-full space-x-4 !ml-0">
                                <MySelect
                                    v-model="loadingRoute.carrierCompanyId"
                                    :options="customerCompanies.items.value"
                                    :loading="customerCompanies.fetching.value"
                                    :name="`routes.${routeIndex}.carrierCompanyId`"
                                    :label="t('carrier')"
                                    group-class="w-full"
                                    searchable
                                    @change="fillRouteRemark(routeIndex)"
                                />

                                <MyInput
                                    v-model="loadingRoute.carrierNumber"
                                    :name="`routes.${routeIndex}.carrierNumber`"
                                    group-class="w-full"
                                    class="carrier-input"
                                    :label="t('carrierNumber')"
                                />

                                <MySelect
                                    v-model="loadingRoute.destinationPortId"
                                    :options="destinationPortOptions"
                                    :name="`routes.${routeIndex}.destinationPortId`"
                                    :label="t('destination')"
                                    group-class="w-full"
                                    searchable
                                />

                                <MyInput
                                    v-model="loadingRoute.remark"
                                    name="remark"
                                    :label="t('remark')"
                                    group-class="w-full"
                                />
                            </div>

                            <MyButton
                                v-tooltip="t('deleteRoute')"
                                type="button"
                                icon
                                size="micro"
                                scheme="danger"
                                class="mb-2 self-end"
                                @click="deleteRoute(routeIndex)"
                            >
                                <mdi:trash-can />
                            </MyButton>
                        </template>

                        <template v-if="isEditing" #items>
                            <div
                                v-for="(loadingListItem, index) in loadingRoute.items"
                                :key="loadingListItem.id"
                                class="grid grid-cols-6 gap-6 text-sm border-b py-2 print:gap-2"
                            >
                                <MyInput
                                    ref="awbInputRefs"
                                    v-model="loadingListItem.awbNumber"
                                    :name="`routes.${routeIndex}.items.${index}.awbNumber`"
                                    :placeholder="t('awbNumber')"
                                />

                                <MyInput
                                    v-model.number="loadingListItem.amount"
                                    type="number"
                                    :name="`routes.${routeIndex}.items.${index}.amount`"
                                    :placeholder="t('amount')"
                                />

                                <MyInput
                                    v-model.number="loadingListItem.weight"
                                    type="float"
                                    :name="`routes.${routeIndex}.items.${index}.weight`"
                                    :placeholder="t('weight')"
                                >
                                    <template #button>
                                        <span
                                            class="absolute top-0 bottom-1 right-2.5 m-auto h-5"
                                            v-text="'kg'"
                                        />
                                    </template>
                                </MyInput>

                                <MyInput
                                    v-model.number="loadingListItem.volume"
                                    type="float"
                                    :name="`routes.${routeIndex}.items.${index}.volume`"
                                    :placeholder="t('volume')"
                                >
                                    <template #button>
                                        <span
                                            class="absolute top-0 bottom-1 right-2.5 m-auto h-5"
                                            v-text="'cbm'"
                                        />
                                    </template>
                                </MyInput>

                                <MyInput
                                    v-model="loadingListItem.description"
                                    :placeholder="t('description')"
                                />

                                <div class="flex items-center space-x-2 justify-between">
                                    <MyInput
                                        v-model="splsAsString(loadingListItem).value"
                                        placeholder="SPL (ABC,DEF,GHI)"
                                    />
                                    <MyButton
                                        v-tooltip="t('deleteItem')"
                                        type="button"
                                        icon
                                        size="micro"
                                        scheme="danger"
                                        class="flex-shrink-0"
                                        @click="deleteItem(routeIndex, index)"
                                    >
                                        <mdi:trash-can />
                                    </MyButton>
                                </div>
                            </div>
                        </template>

                        <template v-if="isEditing" #itemFooterActions>
                            <MyButton
                                class="col-start-6"
                                scheme="primary"
                                type="button"
                                size="small"
                                @click="addItem(routeIndex)"
                            >
                                <mdi:plus-thick class="mr-1" />
                                {{ t('addItem') }}
                            </MyButton>
                        </template>
                    </RouteCard>
                </template>
            </Draggable>

            <!-- GLOBAL TOTALS -->
            <div
                class="grid grid-cols-5 gap-4 text-sm text-primary-500 dark:text-primary-300 border-t pt-4 mt-4"
            >
                <div class="flex flex-col">
                    <span class="text-xs text-stone-500 dark:text-stone-400">
                        {{ t('totalAwbs') }}
                    </span>
                    <span class="font-semibold text-primary-400 dark:text-primary-100">
                        {{ totals.totalAwbs }}
                    </span>
                </div>
                <div class="flex flex-col">
                    <span class="text-xs text-stone-500 dark:text-stone-400">
                        {{ t('totalAmount') }}
                    </span>
                    <span class="font-semibold text-primary-400 dark:text-primary-100">
                        {{ numberFormatter.format(totals.totalAmount) }}
                    </span>
                </div>
                <div class="flex flex-col">
                    <span class="text-xs text-stone-500 dark:text-stone-400">
                        {{ t('totalWeight') }}
                    </span>
                    <span class="font-semibold text-primary-400 dark:text-primary-100">
                        {{ numberFormatter.format(totals.totalWeight) }}
                        {{ t('kg') }}
                    </span>
                </div>
                <div class="flex flex-col">
                    <span class="text-xs text-stone-500 dark:text-stone-400">
                        {{ t('totalVolume') }}
                    </span>
                    <span class="font-semibold text-primary-400 dark:text-primary-100">
                        {{ numberFormatter.format(totals.totalVolume) }}
                        {{ t('cbm') }}
                    </span>
                </div>

                <MyButton
                    v-if="isEditing"
                    scheme="secondary"
                    type="button"
                    size="small"
                    @click="addRoute"
                >
                    <mdi:plus-thick class="mr-1" />
                    {{ t('addRoute') }}
                </MyButton>
            </div>

            <!-- Route validation error -->
            <MyErrorMessage v-if="isEditing" input-name="routes" />

            <!-- Buttons -->
            <div v-if="isEditing" class="flex justify-end mt-6 space-x-2">
                <MyButton scheme="secondary" type="button" @click="cancelEdit">
                    {{ t('cancel') }}
                </MyButton>
                <MyButton scheme="primary" type="submit">
                    {{ t('updateRoutes') }}
                </MyButton>
            </div>
        </MyForm>
    </div>
</template>
